<script setup>
/**
 * Approved messages — the wording WhatsApp has agreed this business may use to start a chat, and
 * everything WhatsApp will tell us about each piece of it.
 *
 * WHY THIS SCREEN EXISTS WHEN CHATWOOT ALREADY HAS A TEMPLATES PAGE. Chatwoot's page
 * (`settings/templates/Index.vue`) is READ-ONLY and reads the SYNCED BLOB: it lists name, status
 * and category, and offers a refresh. That blob is filled by
 * `WhatsappCloudService#fetch_whatsapp_templates`, which asks Graph with NO `fields` parameter —
 * so it structurally cannot carry a quality rating, a time-to-live, a sub-category, a previous or
 * correct category, or a library name, and there is nowhere in the product to edit a template,
 * delete one, or lift a pause.
 *
 * This screen asks Meta for each field by name and adds the lifecycle Meta actually exposes.
 *
 * ── THE FOUR THINGS THE HEADER HAS TO BE HONEST ABOUT ────────────────────────────────
 *
 * Every one of these is a state where a naive screen looks confident and is wrong:
 *
 *   `source: 'stored'`  Meta could not be reached, so this is the copy the platform last synced.
 *                       SAID OUT LOUD. A stale copy presented as live is the same class of lie as
 *                       a fabricated status, which is the defect this whole feature exists against.
 *   `fields: 'basic'`   Meta answered but would not give us the extra fields on this Graph
 *                       version, so there is no quality rating to show — our side is fine and the
 *                       banner says which facts are missing rather than leaving blanks.
 *   `truncated`         the library is bigger than we walked. A list cut off silently tells an
 *                       owner a template does not exist.
 *   `summary`           Meta's own count and ceiling. READ, never printed: Meta's overview page
 *                       says up to 6,000 for a verified portfolio and its own error-code page says
 *                       250 full stop, so a constant here would be wrong for half of all accounts.
 *
 * TEMPLATES ARE APPROVED PER NUMBER, so this is scoped to one and the number is chosen here —
 * `OutboundTemplatesCard`'s finding, restated: a list shown under the wrong number is not a
 * smaller answer, it is a wrong one.
 *
 * THE REFRESH TELLS THE TRUTH ABOUT ITSELF. `POST …/sync_templates` asks the platform to go and
 * refill the blob the REST of the product reads; this page already reads Meta directly. So the
 * button re-reads the live library AND asks for that sync, and says which of the two it did.
 */
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';

import FormsTab from '../components/FormsTab.vue';
import TemplateDeleteDialog from '../components/TemplateDeleteDialog.vue';
import TemplateFilterBar from '../components/TemplateFilterBar.vue';
import TemplateRow from '../components/TemplateRow.vue';
import WasloExplainer from '../components/WasloExplainer.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import WasloSelect from '../components/WasloSelect.vue';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { toIntlLocale } from '../helpers/intl.js';
import { managerUrl } from '../helpers/template-facts.js';
import {
  BLANK_FILTER,
  PAGE_SIZE,
  facets,
  filterTemplates,
  isNarrowed,
} from '../helpers/template-filter.js';
import { useWasloI18n } from '../i18n.js';
import { templatesApi } from '../api/templates.js';

const { t, locale } = useWasloI18n();
const route = useRoute();
const router = useRouter();
const accountId = useWasloAccountId();

// ── which tab ──────────────────────────────────────────────────────────────────────
// FORMS IS THE SECOND TAB OF THIS PAGE, not a nav row. The retired row gave the reason as
// an argument for sitting beside this one: both are one WhatsApp number's inventory at
// Meta, and both are scoped to ONE number. That describes a screen with two lists.
//
// The tab rides in the QUERY (`?tab=forms`), so the route table grows by zero records —
// which matters structurally, not tidily: `SidebarGroup` resolves every child's `to`
// through `router.resolve()` inside a computed during render, and an unregistered name
// throws there and blanks the whole primary navigation. `waslo_forms` keeps its record and
// redirects here.
const TABS = ['messages', 'forms'];

const tab = computed(() => {
  const asked = String(route?.query?.tab ?? '');
  return TABS.includes(asked) ? asked : TABS[0];
});

const onFormsTab = computed(() => tab.value === 'forms');

const tabs = computed(() => [
  { value: 'messages', label: t('WASLO.NAV.TEMPLATES') },
  { value: 'forms', label: t('WASLO.NAV.FORMS') },
]);

const activeTabIndex = computed(() => TABS.indexOf(tab.value));

// `replace`, not `push`: a tab is a view of one screen, not a place you visited, and
// pushing it makes Back walk through every tab the owner tried instead of leaving.
const selectTab = chosen => {
  if (!chosen || chosen.value === tab.value) return;
  router.replace({ query: { ...(route?.query ?? {}), tab: chosen.value } });
};

// `isEmpty: () => false`: a workspace with no WhatsApp number is a MEANING, rendered under the
// explanation, not a blank frame.
const senders = useWasloApi(
  signal => templatesApi.senders(accountId.value, signal),
  { isEmpty: () => false }
);

const senderId = ref('');
const templates = ref([]);
const answer = ref(null);
const isReading = ref(false);
const readFailed = ref(false);
const syncNote = ref('');
const syncError = ref('');
const isSyncing = ref(false);

const filter = ref({ ...BLANK_FILTER });
const visible = ref(PAGE_SIZE);

/** `{ id, action }` while a lifecycle call is in flight, so only that row's buttons lock. */
const working = ref(null);
const removeTarget = ref(null);
const removeFailure = ref('');
const isRemoving = ref(false);
const removeDialog = ref(null);

const senderOptions = computed(() =>
  (senders.data.value ?? []).map(sender => ({
    value: String(sender.id),
    label: sender.phoneNumber
      ? `${sender.name} · ${sender.phoneNumber}`
      : sender.name,
  }))
);

// The number in the link when there is one, else the first number, chosen for her. One number is the common case and a picker she has to
// touch before anything appears reads as an empty screen.
watch(
  senderOptions,
  options => {
    if (senderId.value === '' && options.length) {
      // An Overview row about one message links here with its number (P-4.6).
      const asked = String(route?.query?.inboxId ?? '');
      const linked = options.find(option => option.value === asked);
      senderId.value = linked ? linked.value : options[0].value;
    }
  },
  { immediate: true }
);

const readTemplates = async () => {
  if (senderId.value === '' || accountId.value === null) return;
  isReading.value = true;
  readFailed.value = false;
  const asked = senderId.value;
  try {
    const result = await templatesApi.list(accountId.value, asked);
    // The number may have changed while this was in flight. A list routed back to the wrong
    // sender is the exact wrong answer this screen is scoped to avoid.
    if (asked !== senderId.value) return;
    templates.value = result.templates;
    answer.value = result;
  } catch {
    if (asked !== senderId.value) return;
    templates.value = [];
    answer.value = null;
    readFailed.value = true;
  } finally {
    if (asked === senderId.value) isReading.value = false;
  }
};

watch(senderId, () => {
  templates.value = [];
  answer.value = null;
  syncNote.value = '';
  syncError.value = '';
  filter.value = { ...BLANK_FILTER };
  visible.value = PAGE_SIZE;
  readTemplates();
});

// A narrowed list starts from the top again: "show more" left at 75 while a search has three
// results reads as a screen that lost its own paging.
watch(filter, () => {
  visible.value = PAGE_SIZE;
});

const counts = computed(() => facets(templates.value));
const matching = computed(() => filterTemplates(templates.value, filter.value));
const shown = computed(() => matching.value.slice(0, visible.value));
const narrowed = computed(() => isNarrowed(filter.value));

const summary = computed(() => {
  const given = answer.value?.summary;
  if (!given || given.limit === null) return '';
  return t('WASLO.TEMPLATES.SUMMARY', {
    used: given.used ?? templates.value.length,
    limit: given.limit,
  });
});

const metaManagerUrl = computed(() =>
  managerUrl(answer.value?.businessAccountId ?? '')
);

const lastChecked = computed(() => {
  const when = answer.value?.lastSyncAttemptAt;
  if (!when) return '';
  const at = new Date(when);
  if (Number.isNaN(at.getTime())) return '';
  return t('WASLO.TEMPLATES.LAST_CHECKED', {
    when: at.toLocaleString(toIntlLocale(locale.value), {
      dateStyle: 'medium',
      timeStyle: 'short',
    }),
  });
});

/**
 * Re-reads Meta AND asks the platform to refill the blob the rest of the product reads. Two
 * different things, so the note says both happened rather than implying one.
 */
const refresh = async () => {
  if (isSyncing.value || senderId.value === '') return;
  isSyncing.value = true;
  syncNote.value = '';
  syncError.value = '';
  try {
    await templatesApi.requestSync(accountId.value, senderId.value);
    syncNote.value = t('WASLO.TEMPLATES.REQUESTED');
  } catch {
    syncError.value = t('WASLO.TEMPLATES.REFRESH_FAILED');
  } finally {
    isSyncing.value = false;
  }
  await readTemplates();
};

const openNew = () => {
  if (accountId.value === null || senderId.value === '') return;
  router.push({
    name: 'waslo_template_new',
    params: { accountId: String(accountId.value) },
    query: { inboxId: senderId.value },
  });
};

const openEdit = template => {
  if (accountId.value === null || !template.id) return;
  router.push({
    name: 'waslo_template_edit',
    params: {
      accountId: String(accountId.value),
      templateId: String(template.id),
    },
    // The name rides along so the editor's read is ONE Graph call instead of a walk through the
    // library — which happens inside a Rails request, where this stack has been bitten by
    // `rack-timeout` before. It is an optimisation; the server still selects by id.
    query: { inboxId: senderId.value, name: template.name },
  });
};

const busyFor = template =>
  working.value?.id === template.id ? working.value.action : '';

const unpause = async template => {
  if (working.value || !template.id) return;
  working.value = { id: template.id, action: 'unpause' };
  syncError.value = '';
  syncNote.value = '';
  try {
    await templatesApi.unpause(accountId.value, senderId.value, template.id);
    syncNote.value = t('WASLO.TEMPLATES.ACTIONS.UNPAUSED');
    await readTemplates();
  } catch (error) {
    // Meta's own sentence when Meta refused — this endpoint is documented on one Meta page only
    // and its request and response shapes are not published at all, so whatever Meta says about
    // it is more use to an owner than a sentence of ours guessing.
    syncError.value =
      error?.metaError?.message || t('WASLO.TEMPLATES.ACTIONS.UNPAUSE_FAILED');
  } finally {
    working.value = null;
  }
};

/**
 * The other languages this NAME exists in. Meta's name-only delete takes every one of them, so
 * the dialog cannot offer that scope honestly without knowing which they are.
 */
const otherLanguages = computed(() => {
  const target = removeTarget.value;
  if (!target) return [];
  return templates.value
    .filter(row => row.name === target.name && row.language !== target.language)
    .map(row => row.language);
});

const askRemove = template => {
  removeTarget.value = template;
  removeFailure.value = '';
  removeDialog.value?.open();
};

const remove = async ({ scope }) => {
  const target = removeTarget.value;
  if (!target || isRemoving.value) return;
  isRemoving.value = true;
  removeFailure.value = '';
  try {
    await templatesApi.remove(accountId.value, senderId.value, target.id, {
      name: target.name,
      scope: scope,
    });
    removeDialog.value?.close();
    syncNote.value = t('WASLO.TEMPLATES.REMOVE.DONE', { name: target.name });
    removeTarget.value = null;
    await readTemplates();
  } catch (error) {
    removeFailure.value =
      error?.metaError?.message || t('WASLO.TEMPLATES.REMOVE.FAILED');
  } finally {
    isRemoving.value = false;
  }
};
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.TEMPLATES.TITLE')"
    :is-fetching="onFormsTab ? false : senders.isFetching.value"
    :not-provisioned="onFormsTab ? false : senders.isNotProvisioned.value"
    :error-message="onFormsTab ? '' : senders.errorMessage.value"
    :button-label="
      !onFormsTab && senderOptions.length ? t('WASLO.TEMPLATES.ADD') : ''
    "
    @click="openNew"
    @retry="senders.reload()"
  >
    <!--
      Outside the state branches on purpose, the same place Activity puts its period
      control: the tab you just pressed must not disappear while the list under it loads,
      and the tab you did NOT press must stay reachable when the one you did has failed.
    -->
    <template #controls>
      <div class="flex flex-wrap items-center gap-3 pb-4">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTabIndex"
          :aria-label="t('WASLO.TEMPLATES.TABS.LABEL')"
          data-waslo-templates-tabs
          @tab-changed="selectTab"
        />
      </div>
    </template>

    <template #body>
      <!--
        THE FRAME IS NEUTRAL WHILE FORMS IS OPEN, and the tab renders its own states.
        `FormsTab` makes three reads of its own — one of them out to Meta's Graph API for a
        WABA that rate-limits it — so handing them to this frame would mean this page
        deciding what a tab it does not own is allowed to say. `v-if`, so none of that work
        happens for an owner who came here to fix a template and never pressed Forms, and
        so the Meta read is paid for once rather than on every visit.

        NO PRIMARY BUTTON ON THAT TAB EITHER: the Forms tab authors nothing. A form's
        contents are DERIVED from a task's steps, so the place to change what it asks is the
        task, not a second authoring screen that could disagree with it.
      -->
      <FormsTab v-if="onFormsTab" />

      <div v-else class="flex flex-col gap-8 pb-10">
        <WasloExplainer
          icon="i-lucide-badge-check"
          :title="t('WASLO.TEMPLATES.SPOTLIGHT_TITLE')"
          :note="t('WASLO.TEMPLATES.SPOTLIGHT')"
        />

        <section class="flex flex-col gap-4" data-waslo-templates>
          <SettingsHeader
            :heading="t('WASLO.TEMPLATES.LIST.HEADING')"
            :description="t('WASLO.TEMPLATES.LIST.DESCRIPTION')"
          />

          <div
            v-if="senderOptions.length === 0"
            class="flex items-start w-full gap-4 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-templates-no-sender
          >
            <span
              class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-amber-3"
              aria-hidden="true"
            >
              <span class="size-5 i-lucide-phone-off text-n-amber-11" />
            </span>
            <div class="flex flex-col gap-1">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t('WASLO.TEMPLATES.NO_SENDER.TITLE') }}
              </p>
              <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
                {{ t('WASLO.TEMPLATES.NO_SENDER.BODY') }}
              </p>
            </div>
          </div>

          <template v-else>
            <div class="flex flex-col gap-3 sm:flex-row sm:items-end">
              <WasloSelect
                v-model="senderId"
                class="flex-1 min-w-0"
                :label="t('WASLO.TEMPLATES.SENDER_LABEL')"
                :options="senderOptions"
                data-waslo-templates-sender
              />
              <Button
                class="shrink-0"
                :label="
                  isSyncing
                    ? t('WASLO.TEMPLATES.REFRESHING')
                    : t('WASLO.TEMPLATES.REFRESH')
                "
                icon="i-lucide-refresh-cw"
                variant="faded"
                color="slate"
                size="sm"
                :is-loading="isSyncing"
                :disabled="isSyncing"
                data-waslo-templates-refresh
                @click="refresh"
              />
            </div>

            <!--
              THE COPY WE SYNCED, SAID OUT LOUD. Meta could not be reached, so these rows are as
              current as the platform's last sync — which may be hours old, and which carries none
              of the extra fields. Presenting it as live is the lie this feature was built against.
            -->
            <Banner
              v-if="answer && answer.source === 'stored'"
              color="amber"
              data-waslo-templates-stored
            >
              <span dir="auto">
                {{ t('WASLO.TEMPLATES.STORED') }}
                <bdi v-if="answer.readError?.message">
                  {{ answer.readError.message }}
                </bdi>
              </span>
            </Banner>

            <!--
              WhatsApp answered but this Graph version would not give us the extra fields, so there
              is no quality rating and no delivery window to show. Our side is fine; the banner says
              which facts are missing rather than leaving the panel with blanks in it.
            -->
            <Banner
              v-if="
                answer &&
                answer.source === 'whatsapp' &&
                answer.fields === 'basic'
              "
              color="amber"
              data-waslo-templates-basic-fields
            >
              {{ t('WASLO.TEMPLATES.BASIC_FIELDS') }}
            </Banner>

            <Banner
              v-if="answer && answer.truncated"
              color="amber"
              data-waslo-templates-truncated
            >
              {{ t('WASLO.TEMPLATES.TRUNCATED') }}
            </Banner>

            <Banner v-if="syncNote" color="blue" data-waslo-templates-note>
              {{ syncNote }}
            </Banner>
            <Banner
              v-if="syncError"
              color="ruby"
              data-waslo-templates-sync-error
            >
              <!--
                `dir="auto"` plus `<bdi>`: this can be WhatsApp's OWN sentence, forwarded as Meta
                wrote it, so its direction is unknown and it is sitting inside an Arabic page.
              -->
              <span dir="auto">
                <bdi>{{ syncError }}</bdi>
              </span>
            </Banner>

            <div
              v-if="isReading"
              class="flex items-center justify-center py-8 text-n-slate-11"
              data-waslo-templates-loading
            >
              <Spinner />
            </div>

            <p
              v-else-if="readFailed"
              class="mb-0 text-sm leading-6 text-n-ruby-9"
              data-waslo-templates-error
            >
              {{ t('WASLO.TEMPLATES.UNAVAILABLE') }}
            </p>

            <div
              v-else-if="templates.length === 0"
              class="flex items-start w-full gap-4 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
              data-waslo-templates-empty
            >
              <span
                class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-blue-3"
                aria-hidden="true"
              >
                <span
                  class="size-5 i-lucide-message-square-plus text-n-blue-10"
                />
              </span>
              <div class="flex flex-col items-start gap-3">
                <div class="flex flex-col gap-1">
                  <p class="mb-0 text-sm font-medium text-n-slate-12">
                    {{ t('WASLO.TEMPLATES.EMPTY.TITLE') }}
                  </p>
                  <p
                    class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
                  >
                    {{ t('WASLO.TEMPLATES.EMPTY.BODY') }}
                  </p>
                </div>
                <Button
                  :label="t('WASLO.TEMPLATES.ADD')"
                  icon="i-lucide-plus"
                  size="sm"
                  data-waslo-template-add-first
                  @click="openNew"
                />
              </div>
            </div>

            <template v-else>
              <p
                v-if="summary"
                class="mb-0 text-xs leading-5 text-n-slate-10"
                data-waslo-templates-summary
              >
                {{ summary }}
              </p>

              <TemplateFilterBar
                v-model="filter"
                :facets="counts"
                :showing="matching.length"
                :narrowed="narrowed"
                @clear="filter = { ...BLANK_FILTER }"
              />

              <!-- A filter with no results is a different fact from a library with none. -->
              <div
                v-if="matching.length === 0"
                class="flex flex-col gap-1 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
                data-waslo-templates-none-matching
              >
                <p class="mb-0 text-sm font-medium text-n-slate-12">
                  {{ t('WASLO.TEMPLATES.FILTER.NONE_TITLE') }}
                </p>
                <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
                  {{ t('WASLO.TEMPLATES.FILTER.NONE_BODY') }}
                </p>
              </div>

              <ul v-else class="flex flex-col gap-3 mb-0 list-none">
                <TemplateRow
                  v-for="template in shown"
                  :key="`${template.name}:${template.language}`"
                  :template="template"
                  :fields="answer?.fields ?? 'basic'"
                  :manager-url="metaManagerUrl"
                  :busy="busyFor(template)"
                  :account-id="accountId"
                  :inbox-id="senderId"
                  @edit="openEdit(template)"
                  @unpause="unpause(template)"
                  @remove="askRemove(template)"
                />
              </ul>

              <div v-if="matching.length > shown.length" class="flex">
                <Button
                  :label="
                    t('WASLO.TEMPLATES.FILTER.MORE', {
                      count: matching.length - shown.length,
                    })
                  "
                  variant="faded"
                  color="slate"
                  size="sm"
                  data-waslo-templates-more
                  @click="visible += PAGE_SIZE"
                />
              </div>
            </template>

            <p
              v-if="lastChecked"
              class="mb-0 text-xs leading-5 text-n-slate-10"
              data-waslo-templates-checked
            >
              {{ lastChecked }}
            </p>

            <!--
              WHAT WHATSAPP DOES ON ITS OWN, and every line of it is something an owner is
              otherwise surprised by: a review that takes up to a day, the first sends of a new
              template being held back while WhatsApp watches the reaction, a pause that escalates
              3 hours → 6 hours → switched off for good, and a template nobody has used for a year
              being archived and then deleted 28 days later with no way back.
            -->
            <div
              class="flex flex-col gap-2 px-6 py-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
              data-waslo-templates-lifecycle
            >
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t('WASLO.TEMPLATES.LIFECYCLE.TITLE') }}
              </p>
              <ul class="flex flex-col gap-1.5 ps-0 mb-0 list-none">
                <li
                  v-for="key in ['REVIEW', 'PACING', 'PAUSING', 'ARCHIVAL']"
                  :key="key"
                  class="text-sm leading-6 max-w-[72ch] text-n-slate-11"
                >
                  {{ t(`WASLO.TEMPLATES.LIFECYCLE.${key}`) }}
                </li>
              </ul>
            </div>
          </template>
        </section>
      </div>

      <TemplateDeleteDialog
        ref="removeDialog"
        :template="removeTarget"
        :other-languages="otherLanguages"
        :busy="isRemoving"
        :failure="removeFailure"
        @confirm="remove"
      />
    </template>
  </WasloPageLayout>
</template>
